import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import CurrencyFormat from "react-currency-format";
import moment from "moment";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

const DetailedSalesGraphicalView = ({ salesData }) => {
  // Sales by Package Name
  const packageSalesData = useMemo(() => {
    const packageMap = {};
    salesData.forEach((item) => {
      const pkgName = item.package_name || "Unknown";
      if (!packageMap[pkgName]) {
        packageMap[pkgName] = {
          name: pkgName.length > 30 ? pkgName.substring(0, 30) + "..." : pkgName,
          fullName: pkgName,
          sales: 0,
          quantity: 0,
          netPrice: 0,
          discount: 0,
        };
      }
      packageMap[pkgName].sales += parseFloat(item.order_detail_price || 0);
      packageMap[pkgName].quantity += 1;
      packageMap[pkgName].netPrice += parseFloat(item.order_detail_net_price || 0);
      packageMap[pkgName].discount += parseFloat(item.order_detail_discount || 0);
    });
    return Object.values(packageMap)
      .sort((a, b) => b.netPrice - a.netPrice)
      .slice(0, 10);
  }, [salesData]);

  // Sales by Category
  const categorySalesData = useMemo(() => {
    const categoryMap = {};
    salesData.forEach((item) => {
      const catName = item.category_name || "Unknown";
      if (!categoryMap[catName]) {
        categoryMap[catName] = {
          name: catName,
          sales: 0,
          quantity: 0,
          netPrice: 0,
        };
      }
      categoryMap[catName].sales += parseFloat(item.order_detail_price || 0);
      categoryMap[catName].quantity += 1;
      categoryMap[catName].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    return Object.values(categoryMap).sort((a, b) => b.netPrice - a.netPrice);
  }, [salesData]);

  // Sales by Payment Method
  const paymentMethodData = useMemo(() => {
    const paymentMap = {};
    salesData.forEach((item) => {
      const method = item.payment_method || "N/A";
      if (!paymentMap[method]) {
        paymentMap[method] = {
          name: method,
          count: 0,
          netPrice: 0,
        };
      }
      paymentMap[method].count += 1;
      paymentMap[method].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    return Object.values(paymentMap).sort((a, b) => b.netPrice - a.netPrice);
  }, [salesData]);

  // Sales by Seller (Sold By)
  const sellerData = useMemo(() => {
    const sellerMap = {};
    salesData.forEach((item) => {
      const seller = item.sold_by || "Unknown";
      if (!sellerMap[seller]) {
        sellerMap[seller] = {
          name: seller,
          count: 0,
          netPrice: 0,
        };
      }
      sellerMap[seller].count += 1;
      sellerMap[seller].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    return Object.values(sellerMap).sort((a, b) => b.netPrice - a.netPrice).slice(0, 10);
  }, [salesData]);

  // Sales by Date (Daily trend)
  const dailySalesData = useMemo(() => {
    const dateMap = {};
    salesData.forEach((item) => {
      const date = moment(item.sale_date).format("DD/MM/YYYY");
      if (!dateMap[date]) {
        dateMap[date] = {
          date: date,
          sales: 0,
          netPrice: 0,
          count: 0,
        };
      }
      dateMap[date].sales += parseFloat(item.order_detail_price || 0);
      dateMap[date].netPrice += parseFloat(item.order_detail_net_price || 0);
      dateMap[date].count += 1;
    });
    return Object.values(dateMap).sort((a, b) => moment(a.date, "DD/MM/YYYY") - moment(b.date, "DD/MM/YYYY"));
  }, [salesData]);

  // Sales by Duration
  const durationData = useMemo(() => {
    const durationMap = {};
    salesData.forEach((item) => {
      const duration = item.package_duration || 0;
      let durationLabel = "";
      if (duration <= 15) durationLabel = "Up to 15 Days";
      else if (duration <= 30) durationLabel = "Monthly";
      else if (duration <= 90) durationLabel = "1-3 Months";
      else if (duration <= 180) durationLabel = "3-6 Months";
      else durationLabel = "6+ Months";
      
      if (!durationMap[durationLabel]) {
        durationMap[durationLabel] = {
          name: durationLabel,
          count: 0,
          netPrice: 0,
        };
      }
      durationMap[durationLabel].count += 1;
      durationMap[durationLabel].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    return Object.values(durationMap);
  }, [salesData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border shadow rounded">
          <p className="fw-bold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: <CurrencyFormat value={entry.value} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!salesData || salesData.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No data available for graphical view</p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row g-3">
        {/* Top Packages Sales */}
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">Top 10 Packages by Sales</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={packageSalesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="netPrice" fill="#0088FE" name="Net Price" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sales by Category - Pie Chart */}
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0">Sales by Category</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={categorySalesData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="netPrice"
                  >
                    {categorySalesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Daily Sales Trend */}
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-info text-white">
              <h6 className="mb-0">Daily Sales Trend</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={dailySalesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="netPrice" stroke="#8884d8" fill="#8884d8" name="Net Price" />
                  <Area type="monotone" dataKey="sales" stroke="#82ca9d" fill="#82ca9d" name="Total Sales" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sales by Payment Method */}
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-header bg-warning text-dark">
              <h6 className="mb-0">Sales by Payment Method</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={paymentMethodData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="netPrice"
                  >
                    {paymentMethodData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top Sellers */}
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-header bg-danger text-white">
              <h6 className="mb-0">Top 10 Sellers by Sales</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={sellerData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="netPrice" fill="#FF8042" name="Net Price" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sales by Duration */}
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-header bg-secondary text-white">
              <h6 className="mb-0">Sales by Package Duration</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={durationData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="netPrice" fill="#8884d8" name="Net Price" />
                  <Bar dataKey="count" fill="#82ca9d" name="Quantity" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sales by Category - Bar Chart */}
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-header bg-dark text-white">
              <h6 className="mb-0">Sales by Category (Bar Chart)</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={categorySalesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="netPrice" fill="#0088FE" name="Net Price" />
                  <Bar dataKey="sales" fill="#00C49F" name="Total Sales" />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedSalesGraphicalView;

